import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Popover, IconButton, TextField,
  Autocomplete, Chip, Stack, Grid, Divider
} from '@mui/material';
import {
  FilterAlt as FilterAltIcon,
  Business as AgentIcon,
  Home as ProjectIcon,
  Person as LeadIcon,
  Close as CloseIcon,
  EventAvailable as StatusIcon,
  DirectionsCar as TransportationIcon,
  Videocam as VisitTypeIcon
} from '@mui/icons-material';

import AgentSelect from '../../components/common/AgentSelect';

// Common styles for chips
const chipStyles = {
  height: '24px',
  '& .MuiChip-label': {
    fontSize: '0.75rem',
    px: 1,
    fontWeight: 400
  }
};

// Common styles for section headers
const sectionHeaderStyles = {
  fontSize: '0.75rem',
  color: 'text.secondary',
  fontWeight: 500,
  textTransform: 'none'
};

// Common styles for Autocomplete
const autocompleteStyles = {
  '& .MuiInputBase-root': { 
    fontSize: '0.75rem',
    minHeight: '32px',
    fontWeight: 400
  },
  '& .MuiInputBase-input': {
    fontSize: '0.75rem',
    fontWeight: 400
  },
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)'
  },
  '& .MuiAutocomplete-popupIndicator': {
    padding: '2px'
  },
  '& .MuiAutocomplete-clearIndicator': {
    padding: '2px'
  }
};

const SiteVisitFilter = ({
  open, 
  anchorEl, 
  onClose,
  agents = [],
  projects = [],
  statusOptions = [],
  transportationOptions = [],
  visitTypeOptions = [],
  leadInterestLevelOptions = [],
  appliedFilters,
  setAppliedFilters,
  fetchSiteVisits,
  dateRangeState 
}) => {
  // State for filters
  const [filters, setFilters] = useState({});
  const [initialFilters, setInitialFilters] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Sync filters with applied filters when modal opens
  useEffect(() => {
    if (open) {
      // Normalize applied filters to match UI expectations
      const normalizedFilters = { ...appliedFilters };

      // Convert backend filter names to frontend UI names
      if (normalizedFilters.agents) {
        normalizedFilters.agent_ids = normalizedFilters.agents;
        delete normalizedFilters.agents;
      }
      if (normalizedFilters.projects) {
        normalizedFilters.project_ids = normalizedFilters.projects;
        delete normalizedFilters.projects;
      }
      if (normalizedFilters.statuses) {
        normalizedFilters.status = normalizedFilters.statuses;
        delete normalizedFilters.statuses;
      }
      if (normalizedFilters.visit_types) {
        normalizedFilters.visit_type = normalizedFilters.visit_types;
        delete normalizedFilters.visit_types;
      }
      if (normalizedFilters.transportation_types) {
        normalizedFilters.transportation_type = normalizedFilters.transportation_types;
        delete normalizedFilters.transportation_types;
      }

      // Array fields that should always be arrays in the UI
      const arrayFields = ['status', 'agent_ids', 'project_ids', 'visit_type', 'transportation_type'];

      arrayFields.forEach(field => {
        if (normalizedFilters[field] !== undefined && normalizedFilters[field] !== null) {
          // Convert single values to arrays
          if (!Array.isArray(normalizedFilters[field])) {
            normalizedFilters[field] = [normalizedFilters[field]];
          }
        }
      });

      // When modal opens, reset the filter state to match applied filters
      setFilters(normalizedFilters || {});
      setInitialFilters(normalizedFilters || {});
      setHasChanges(false);
    }
  }, [open, appliedFilters]);

  // Add effect to check for changes whenever filters change
  useEffect(() => {
    // Deep comparison of filters
    const initialKeys = Object.keys(initialFilters);
    const currentKeys = Object.keys(filters);

    // Check if keys are different
    if (initialKeys.length !== currentKeys.length) {
      setHasChanges(true);
      return;
    }

    // Check if values are different
    let changed = false;

    for (const key of currentKeys) {
      const initialValue = initialFilters[key];
      const currentValue = filters[key];

      if (Array.isArray(currentValue)) {
        // Compare arrays
        if (!initialValue || !Array.isArray(initialValue) ||
          initialValue.length !== currentValue.length ||
          !initialValue.every((val, i) => val === currentValue[i])) {
          changed = true;
          break;
        }
      } else {
        // Compare other values
        if (initialValue !== currentValue) {
          changed = true;
          break;
        }
      }
    }

    setHasChanges(changed);
  }, [filters, initialFilters]);

  // Event handlers for apply filters view
  const handleFilterChange = (filterKey, newValue) => {
    // Special handling for array fields to ensure they're always arrays
    if (['statuses', 'agent_ids', 'project_ids', 'visit_types', 'transportation_type'].includes(filterKey)) {
      // Ensure we have an array
      const arrayValue = Array.isArray(newValue) ? newValue : (newValue ? [newValue] : []);

      setFilters(prev => ({
        ...prev,
        [filterKey]: arrayValue
      }));

      setHasChanges(true);
      return;
    }

    // Default handling for other filter types
    setFilters(prev => ({
      ...prev,
      [filterKey]: newValue
    }));

    setHasChanges(true);
  };

  const handleClearFilters = () => {
    // Clear current filters state without affecting applied filters
    setFilters({});

    // Set hasChanges to true since we've changed the filters from their initial state
    setHasChanges(true);
  };

  const handleApplyFilters = () => {
    // Make a copy of the current filters
    const filtersToApply = { ...filters };
    console.log('filtersToApply ', filtersToApply);

    // Remove any empty array filters
    Object.keys(filtersToApply).forEach(key => {
      if (Array.isArray(filtersToApply[key]) && filtersToApply[key].length === 0) {
        delete filtersToApply[key];
      }
    });

    // Handle special field conversions between frontend and backend
    if (filtersToApply.agent_ids && Array.isArray(filtersToApply.agent_ids)) {
      // Ensure agent_ids values are strings or numbers, not objects
      const agentIds = filtersToApply.agent_ids.map(agent =>
        typeof agent === 'object' ? agent.agent_id || agent.id : agent
      );
      // Backend expects 'agents' not 'agent_ids'
      filtersToApply.agents = agentIds;
      delete filtersToApply.agent_ids;
    }

    if (filtersToApply.project_ids && Array.isArray(filtersToApply.project_ids)) {
      // Ensure project_ids values are strings or numbers, not objects
      const projectIds = filtersToApply.project_ids.map(project =>
        typeof project === 'object' ? project.id : project
      );
      filtersToApply.projects = projectIds;
      delete filtersToApply.project_ids;
    }

    // Map frontend filter names to backend expected names
    if (filtersToApply.visit_type) {
      filtersToApply.visit_types = filtersToApply.visit_type;
      delete filtersToApply.visit_type;
    }
    
    if (filtersToApply.status) {
      filtersToApply.statuses = filtersToApply.status;
      delete filtersToApply.status;
    }
    
    if (filtersToApply.transportation_type) {
      filtersToApply.transportation_types = filtersToApply.transportation_type;
      delete filtersToApply.transportation_type;
    }

    // Update applied filters in context
    setAppliedFilters(filtersToApply);
    setInitialFilters(filtersToApply);
    setHasChanges(false);
    
    // Call the API to fetch filtered site visits with forceRefresh to bypass duplicate prevention
    fetchSiteVisits(filtersToApply, dateRangeState, null, true);

    // Close the filter modal
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 500,
          maxHeight: '90vh',
          p: 0,
          borderRadius: 1,
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterAltIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Site Visit Filters
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Filters List */}
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        <>
          {/* Agent Filters */}
          {agents && agents.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AgentIcon fontSize="small" />
                  <Typography sx={{
                    ml: 1,
                    ...sectionHeaderStyles,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    Agents
                  </Typography>
                </Box>

                {/* Show clear button if filter is active */}
                {filters.agent_ids && filters.agent_ids.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.agent_ids;
                      setFilters(newFilters);
                      setHasChanges(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <AgentSelect
                multiple={true}
                value={Array.isArray(filters.agent_ids) ? filters.agent_ids : []}
                onChange={(agentIds) => {
                  handleFilterChange('agent_ids', agentIds);
                }}
                label="Select Agents"
                size="small"
                sx={{
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
                    }
                  }
                }}
                agents={agents}
              />
            </Box>
          )}

          {/* Project Filters */}
          {projects && projects.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ProjectIcon fontSize="small" />
                  <Typography sx={{
                    ml: 1,
                    ...sectionHeaderStyles,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    Projects
                  </Typography>
                </Box>

                {/* Show clear button if filter is active */}
                {filters.project_ids && filters.project_ids.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.project_ids;
                      setFilters(newFilters);
                      setHasChanges(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Autocomplete
                multiple
                size="small"
                options={projects}
                value={Array.isArray(filters.project_ids) ? filters.project_ids : []}
                onChange={(e, newValue) => handleFilterChange('project_ids', newValue)}
                getOptionLabel={(option) => option.name || option}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={{
                  ...autocompleteStyles,
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search projects..."
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      label={option.name || option}
                      size="small"
                      sx={{
                        ...chipStyles,
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)'
                        }
                      }}
                    />
                  ))
                }
              />
            </Box>
          )}

          {/* Status Filters */}
          {statusOptions && statusOptions.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StatusIcon fontSize="small" />
                  <Typography sx={{
                    ml: 1,
                    ...sectionHeaderStyles,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    Status
                  </Typography>
                </Box>

                {/* Show clear button if filter is active */}
                {filters.status && filters.status.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.status;
                      setFilters(newFilters);
                      setHasChanges(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {statusOptions.map((status) => {
                  const selected = Array.isArray(filters.status) && filters.status.includes(status.value);
                  return (
                    <Chip
                      key={status.value}
                      label={status.label}
                      size="small"
                      onClick={() => {
                        const currentStatus = Array.isArray(filters.status) ? filters.status : [];
                        if (selected) {
                          handleFilterChange('status', currentStatus.filter(s => s !== status.value));
                        } else {
                          handleFilterChange('status', [...currentStatus, status.value]);
                        }
                      }}
                      sx={{
                        bgcolor: selected ? '#FFA726' : '#f5f5f5',
                        color: selected ? '#fff' : '#222',
                        border: 'none',
                        fontSize: '0.7rem',
                        height: '24px',
                        borderRadius: 2,
                        fontWeight: 500,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        '& .MuiChip-label': { px: 1 },
                        mb: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: selected ? '#FFA726' : '#e0e0e0',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Visit Type Filters */}
          {visitTypeOptions && visitTypeOptions.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <VisitTypeIcon fontSize="small" />
                  <Typography sx={{
                    ml: 1,
                    ...sectionHeaderStyles,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    Visit Type
                  </Typography>
                </Box>

                {/* Show clear button if filter is active */}
                {filters.visit_type && filters.visit_type.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.visit_type;
                      setFilters(newFilters);
                      setHasChanges(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {visitTypeOptions.map((type) => {
                  const selected = Array.isArray(filters.visit_type) && filters.visit_type.includes(type.value);
                  return (
                    <Chip
                      key={type.value}
                      label={type.label}
                      size="small"
                      onClick={() => {
                        const currentVisitType = Array.isArray(filters.visit_type) ? filters.visit_type : [];
                        if (selected) {
                          handleFilterChange('visit_type', currentVisitType.filter(v => v !== type.value));
                        } else {
                          handleFilterChange('visit_type', [...currentVisitType, type.value]);
                        }
                      }}
                      sx={{
                        bgcolor: selected ? '#66BB6A' : '#f5f5f5',
                        color: selected ? '#fff' : '#222',
                        border: 'none',
                        fontSize: '0.7rem',
                        height: '24px',
                        borderRadius: 2,
                        fontWeight: 500,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        '& .MuiChip-label': { px: 1 },
                        mb: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: selected ? '#66BB6A' : '#e0e0e0',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Transportation Type Filters */}
          {transportationOptions && transportationOptions.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TransportationIcon fontSize="small" />
                  <Typography sx={{
                    ml: 1,
                    ...sectionHeaderStyles,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    Transportation Type
                  </Typography>
                </Box>

                {/* Show clear button if filter is active */}
                {filters.transportation_type && filters.transportation_type.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.transportation_type;
                      setFilters(newFilters);
                      setHasChanges(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {transportationOptions.map((transport) => {
                  const selected = Array.isArray(filters.transportation_type) && filters.transportation_type.includes(transport.value);
                  return (
                    <Chip
                      key={transport.value}
                      label={transport.label}
                      size="small"
                      onClick={() => {
                        const currentTransportType = Array.isArray(filters.transportation_type) ? filters.transportation_type : [];
                        if (selected) {
                          handleFilterChange('transportation_type', currentTransportType.filter(t => t !== transport.value));
                        } else {
                          handleFilterChange('transportation_type', [...currentTransportType, transport.value]);
                        }
                      }}
                      sx={{
                        bgcolor: selected ? '#42A5F5' : '#f5f5f5',
                        color: selected ? '#fff' : '#222',
                        border: 'none',
                        fontSize: '0.7rem',
                        height: '24px',
                        borderRadius: 2,
                        fontWeight: 500,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        '& .MuiChip-label': { px: 1 },
                        mb: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: selected ? '#42A5F5' : '#e0e0e0',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      </Box>

      {/* Footer Buttons */}
      <Box sx={{
        p: 2,
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        display: 'flex',
        justifyContent: 'flex-end',
        bgcolor: '#fff'
      }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleClearFilters}
          sx={{ mr: 1, borderRadius: 2 }}
          // Only enable if there are filters to clear
          disabled={Object.keys(filters).length === 0}
        >
          Clear
        </Button>
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={handleApplyFilters}
          sx={{
            bgcolor: '#00875A',
            '&:hover': { bgcolor: '#006644' },
            borderRadius: 2
          }}
          // Only enable if there are changes to apply
          disabled={!hasChanges && Object.keys(appliedFilters || {}).length === 0}
        >
          Apply
        </Button>
      </Box>
    </Popover>
  );
};

export default SiteVisitFilter;